import type { App } from '@urbanmetrix/sdk';
import type { Treaty } from '@elysiajs/eden';
import { treaty } from '@elysiajs/eden';

export const API_BASE_URL = import.meta.env.VITE_SERVER_URL || window.origin;

export const apiClient: Treaty.Create<App> = treaty<App>(API_BASE_URL, {
  fetch: {
    credentials: 'include',
  },
});

export function buildApiUrl(path: string) {
  return `${API_BASE_URL}${path}`;
}

export type ApiDownloadResult = {
  blob: Blob;
  fileName?: string;
  contentType?: string | null;
};

export function getApiErrorMessage(error: unknown, fallback = 'An error occurred') {
  if (!error || typeof error !== 'object') {
    return fallback;
  }
  if ('response' in error && error.response && typeof error.response === 'object' && 'data' in error.response) {
    const responseData = error.response.data;
    if (
      responseData &&
      typeof responseData === 'object' &&
      'message' in responseData &&
      typeof responseData.message === 'string' &&
      responseData.message.trim()
    ) {
      return responseData.message;
    }
  }
  if ('error' in error && error.error) {
    if (typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }
    if (typeof error.error === 'object' && 'message' in error.error) {
      const nestedMessage = error.error.message;
      if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
        return nestedMessage;
      }
    }
  }
  if ('message' in error && typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function getApiErrorStatus(error: unknown, status?: number): number | undefined {
  if (typeof status === 'number') return status;
  if (!error || typeof error !== 'object') return undefined;
  if ('status' in error && typeof error.status === 'number') return error.status;
  return undefined;
}

export function navigateToNotFound() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/notfound') return;

  window.location.replace('/notfound');
}

export function throwApiError(error: unknown, status?: number, navigateToNotFoundOn404 = false): never {
  const resolvedStatus = getApiErrorStatus(error, status);

  if (navigateToNotFoundOn404 && resolvedStatus === 404) {
    navigateToNotFound();
  }

  if (resolvedStatus && error && typeof error === 'object' && !('status' in error)) {
    throw { ...error, status: resolvedStatus };
  }

  throw error;
}

export async function apiFetch(path: string, init?: RequestInit) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
  });

  if (!response.ok) {
    let errorBody: unknown = null;

    try {
      errorBody = await response.json();
    } catch {
      errorBody = null;
    }

    throwApiError(errorBody, response.status);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

function extractFileNameFromContentDisposition(headerValue: string | null): string | undefined {
  if (!headerValue) return undefined;

  const utf8Match = headerValue.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const asciiMatch = headerValue.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  return (asciiMatch?.[1] ?? asciiMatch?.[2])?.trim();
}

export async function apiDownload(path: string, init?: RequestInit): Promise<ApiDownloadResult> {
  const response = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include', ...init });
  if (!response.ok) {
    let errorBody: unknown = null;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = null;
    }
    throwApiError(errorBody, response.status);
  }

  return {
    blob: await response.blob(),
    fileName: extractFileNameFromContentDisposition(response.headers.get('content-disposition')),
    contentType: response.headers.get('content-type'),
  };
}
